/**
 * 📸 VEHICLE IMAGE UPLOADER
 * Optimized image upload component with compression and resizing
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  ZoomIn,
  CheckCircle,
  Error as ErrorIcon,
  PhotoCamera,
  Star,
  StarBorder,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import vehicleImageService from '@/services/vehicleImageService';

interface VehicleImage {
  id?: string;
  url: string;
  category: 'front' | 'back' | 'side' | 'interior' | 'dashboard' | 'other';
  isPrimary: boolean;
  file?: File;
  isNew?: boolean;
}

interface VehicleImageUploaderProps {
  vehicleId: string;
  existingImages?: VehicleImage[];
  onImagesChange?: (images: VehicleImage[]) => void;
  onUpload?: (images: VehicleImage[]) => Promise<void>;
  maxImages?: number;
  maxSizeKB?: number;
}

export const VehicleImageUploader: React.FC<VehicleImageUploaderProps> = ({
  vehicleId,
  existingImages = [],
  onImagesChange,
  onUpload,
  maxImages = 10,
  maxSizeKB = 800, // Max 800KB per image after compression
}) => {
  const [images, setImages] = useState<VehicleImage[]>(existingImages);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null); // Track which image is being deleted
  const [settingPrimary, setSettingPrimary] = useState<string | null>(null); // Track which image is being set as primary
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compress and resize image
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Resize to max 1920x1080 while maintaining aspect ratio
          const maxWidth = 1920;
          const maxHeight = 1080;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Try different quality levels to meet size requirement
          let quality = 0.9;
          let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

          // Reduce quality until file size is under maxSizeKB
          while (compressedDataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          }

          resolve(compressedDataUrl);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);

    try {
      const newImages: VehicleImage[] = [];

      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        // Validate file size (max 10MB original)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }

        try {
          // Compress image
          const compressedDataUrl = await compressImage(file);

          newImages.push({
            url: compressedDataUrl,
            category: 'other',
            isPrimary: images.length === 0 && newImages.length === 0,
            file,
            isNew: true,
          });
        } catch (err) {
          console.error(`Failed to compress ${file.name}:`, err);
          toast.error(`Failed to process ${file.name}`);
        }
      }

      const updatedImages = [...images, ...newImages];
      setImages(updatedImages);
      onImagesChange?.(updatedImages);

      toast.success(`${newImages.length} image(s) added and compressed`);
    } catch (err) {
      console.error('Error processing images:', err);
      toast.error('Failed to process images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove image
  const handleRemoveImage = async (index: number) => {
    const imageToRemove = images[index];

    // If image has an ID, it exists on the backend - delete it there
    if (imageToRemove.id && !imageToRemove.isNew) {
      const confirmDelete = window.confirm('Are you sure you want to delete this image? This action cannot be undone.');
      if (!confirmDelete) return;

      setDeleting(imageToRemove.id);

      try {
        const response = await vehicleImageService.deleteVehicleImage(vehicleId, imageToRemove.id);

        if (!response.success) {
          toast.error(response.error || 'Failed to delete image');
          console.error('❌ Delete failed:', response.error);
          return;
        }

        toast.success('Image deleted successfully from backend');
      } finally {
        setDeleting(null);
      }
    }

    // Remove from local state
    const updatedImages = images.filter((_, i) => i !== index);

    // If removed image was primary, make first image primary
    if (imageToRemove.isPrimary && updatedImages.length > 0) {
      updatedImages[0].isPrimary = true;
    }

    setImages(updatedImages);
    onImagesChange?.(updatedImages);
  };

  // Set primary image
  const handleSetPrimary = async (index: number) => {
    const imageToSetPrimary = images[index];

    // If image is already uploaded to backend (has ID and is not new), call backend API
    if (imageToSetPrimary.id && !imageToSetPrimary.isNew) {
      setSettingPrimary(imageToSetPrimary.id);

      try {
        const response = await vehicleImageService.setPrimaryImage(vehicleId, imageToSetPrimary.id);

        if (!response.success) {
          toast.error(response.error || 'Failed to set cover image');
          console.error('❌ Set primary failed:', response.error);
          return;
        }

        toast.success('Cover image updated successfully');

        // Update local state to reflect the change
        const updatedImages = images.map((img, i) => ({
          ...img,
          isPrimary: i === index,
        }));
        setImages(updatedImages);
        onImagesChange?.(updatedImages);
      } catch (error) {
        console.error('❌ Error setting primary:', error);
        toast.error('Failed to set cover image');
      } finally {
        setSettingPrimary(null);
      }
    } else {
      // For new images (not yet uploaded), just update local state
      const updatedImages = images.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      }));
      setImages(updatedImages);
      onImagesChange?.(updatedImages);
      toast.success('Cover image will be set when you upload');
    }
  };

  // Update image category
  const handleCategoryChange = (index: number, category: VehicleImage['category']) => {
    const updatedImages = [...images];
    updatedImages[index].category = category;
    setImages(updatedImages);
    onImagesChange?.(updatedImages);
  };

  // Upload images to backend
  const handleUpload = async () => {
    if (!onUpload) {
      toast.error('Upload handler not configured');
      return;
    }

    setUploading(true);
    try {
      await onUpload(images);
      toast.success('Images uploaded successfully');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const newImagesCount = images.filter(img => img.isNew).length;

  return (
    <Box>
      {/* Upload Button */}
      <Box sx={{ mb: 3 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<PhotoCamera />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || images.length >= maxImages}
          >
            Add Images
          </Button>

          {newImagesCount > 0 && onUpload && (
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleUpload}
              disabled={uploading}
            >
              Upload {newImagesCount} New Image{newImagesCount > 1 ? 's' : ''}
            </Button>
          )}

          <Typography variant="caption" color="text.secondary">
            {images.length} / {maxImages} images • Max {maxSizeKB}KB per image
          </Typography>
        </Stack>

        {uploading && <LinearProgress sx={{ mt: 2 }} />}
      </Box>

      {/* Info Alerts */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Image Optimization:</strong> Images are automatically compressed to under {maxSizeKB}KB and resized to 1920x1080 max. This saves storage space and improves loading speed.
        </Typography>
      </Alert>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>⭐ Cover Image:</strong> Click the star icon to set which image appears as the main cover photo on vehicle listings. The cover image is what renters see first!
        </Typography>
      </Alert>

      {/* Images Grid */}
      {images.length > 0 ? (
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card
                sx={{
                  position: 'relative',
                  border: image.isPrimary ? '3px solid' : 'none',
                  borderColor: image.isPrimary ? 'warning.main' : 'transparent',
                  boxShadow: image.isPrimary ? '0 4px 20px rgba(255, 193, 7, 0.3)' : undefined,
                }}
              >
                {image.isPrimary && (
                  <Chip
                    icon={<Star />}
                    label="COVER IMAGE"
                    color="warning"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 1,
                      fontWeight: 'bold',
                    }}
                  />
                )}

                {image.isNew && (
                  <Chip
                    label="New"
                    color="success"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                    }}
                  />
                )}

                <CardMedia
                  component="img"
                  height="200"
                  image={image.url}
                  alt={`Vehicle ${image.category}`}
                  sx={{ cursor: 'pointer', objectFit: 'cover' }}
                  onClick={() => setPreviewImage(image.url)}
                />

                <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', flexDirection: 'column', gap: 1 }}>
                  {/* Category Selection */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', width: '100%' }}>
                    {['front', 'back', 'side', 'interior', 'dashboard', 'other'].map((cat) => (
                      <Chip
                        key={cat}
                        label={cat}
                        size="small"
                        variant={image.category === cat ? 'filled' : 'outlined'}
                        onClick={() => handleCategoryChange(index, cat as VehicleImage['category'])}
                        sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 0.5, width: '100%', justifyContent: 'space-between' }}>
                    {/* Set as Cover Button */}
                    {!image.isPrimary ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={settingPrimary === image.id ? <CircularProgress size={14} /> : <StarBorder />}
                        onClick={() => handleSetPrimary(index)}
                        disabled={settingPrimary === image.id}
                        sx={{ flex: 1 }}
                      >
                        {settingPrimary === image.id ? 'Setting...' : 'Set as Cover'}
                      </Button>
                    ) : (
                      <Chip
                        icon={<Star />}
                        label="Current Cover"
                        color="warning"
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    )}

                    {/* Action Icons */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => setPreviewImage(image.url)}
                        title="View full size"
                      >
                        <ZoomIn fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveImage(index)}
                        title="Delete image"
                        disabled={deleting === image.id}
                      >
                        {deleting === image.id ? (
                          <CircularProgress size={16} color="error" />
                        ) : (
                          <Delete fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 6,
            textAlign: 'center',
            bgcolor: 'grey.50',
            cursor: 'pointer',
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <PhotoCamera sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Images Yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>
            Click to upload vehicle images
          </Typography>
          <Typography variant="caption" color="text.disabled">
            💡 First image uploaded will automatically be set as the cover image
          </Typography>
        </Box>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={previewImage}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '80vh' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewImage(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleImageUploader;
